import React from "react";
import {
  Code2,
  Zap,
  TrendingUp,
  Layers,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ShieldCheck,
  LayoutDashboard,
  Globe,
  Database,
  Building2,
  Users2,
  Settings2,
  Smartphone,
  Server,
  Blocks,
  Terminal
} from "lucide-react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "../Components/Navbar";
import Call from "../Components/Call";
import Footer from "../Components/Footer";
import ChatbotWidget from "./ChatComponent";
import TrustStatement from "../Components/TrustStatement";
import "../Styling/CE.css";

gsap.registerPlugin(ScrollTrigger);

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0,
      delayChildren: 0
    }
  }
};

const itemVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0 }
  }
};

export default function CE() {
  const [currentFeature, setCurrentFeature] = React.useState(0);
  const totalFeatures = 3;
  const mainRef = React.useRef(null);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % totalFeatures);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const ctx = gsap.context((self) => {
      // Floating Animation for Cards
      gsap.to(".main-card", {
        y: -20,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-1", {
        y: 15,
        x: 10,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-2", {
        y: -15,
        x: -10,
        duration: 3.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      // Background Blobs
      gsap.to(".CE-blob", {
        y: "30%",
        rotation: 10,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: {
          each: 2,
          from: "random"
        }
      });

      // Mesh Overlay Rotation
      gsap.to(".CE-mesh-overlay", {
        rotation: 360,
        duration: 120,
        repeat: -1,
        ease: "linear"
      });

      ScrollTrigger.refresh();
    }, mainRef);

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      ctx.revert();
      clearTimeout(refreshTimer);
    };
  }, []);

  const nextFeature = () => setCurrentFeature((prev) => (prev + 1) % totalFeatures);
  const prevFeature = () => setCurrentFeature((prev) => (prev === 0 ? totalFeatures - 1 : prev - 1));
  const goToFeature = (index) => setCurrentFeature(index);

  return (
    <div className="CE-page-wrapper" ref={mainRef}>
      <Navbar />

      {/* HERO SECTION (CINEMATIC) */}
      <section className="CE-header-section">
        {/* Background Effects */}
        <div className="CE-mesh-container">
          <div className="CE-mesh-overlay"></div>
        </div>
        <div className="CE-blob blob-magenta"></div>
        <div className="CE-blob blob-purple"></div>
        <div className="CE-blob blob-blue"></div>

        <div className="container">
          <div className="CE-hero-layout">
            <motion.div
              className="CE-hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div className="CE-header-badge" variants={itemVariants}>
                <Code2 size={16} />
                <span>Custom Software Engineering</span>
              </motion.div>

              <motion.h1 className="CE-header-title" variants={itemVariants}>
                Production‑Grade Software Built Around Your <span className="magenta-gradient">Business Logic</span>
              </motion.h1>

              <motion.p className="CE-header-desc" variants={itemVariants}>
                We engineer secure, scalable software platforms tailored to your operational requirements, security needs, and long‑term roadmap. No templates. No shortcuts. Systems designed for real usage and scale.
              </motion.p>

              <motion.div className="CE-hero-ctas" variants={itemVariants}>
                <a
                  href="https://studio.digitaldreamworksagency.com/access"
                  className="CE-btn-hero"
                  onClick={() => {
                    if (window.fbq) {
                      window.fbq('trackCustom', 'RequestDemoOpened', {
                        source: 'ce_hero',
                        page_title: 'Custom Engineering (CE)'
                      });
                    }
                  }}
                >
                  Request Demo Access <ArrowRight size={18} />
                </a>
                <div className="CE-trust">
                  <ShieldCheck size={16} color="#d946ef" />
                  <span>Bank-Grade Security</span>
                </div>
              </motion.div>
            </motion.div>

            <div className="CE-hero-visual">
              <div className="visual-card main-card">
                <Terminal size={64} strokeWidth={1.5} />
                <span>Core Logic</span>
              </div>
              <div className="visual-card secondary-card card-1">
                <LayoutDashboard size={32} color="#d946ef" />
                <span>Admin Ops</span>
              </div>
              <div className="visual-card secondary-card card-2">
                <Blocks size={32} color="#8b5cf6" />
                <span>Modular API</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SLIDER SECTION */}
      <section className="CE-slider-section">
        <div className="container">
          <div className="CE-slider-header">
            <h2 className="CE-slider-title">Core Systems</h2>
            <p className="CE-slider-subtitle">Modular architecture designed for enterprise-grade performance.</p>
          </div>

          <div className="CE-carousel">
            <div className="CE-carousel-viewport">
              {[
                {
                  title: "Platform Architecture",
                  desc: "Custom software foundations designed for security, scalability, and longevity.",
                  badge: "Scale-First",
                  icon: <Layers size={42} />
                },
                {
                  title: "API‑First Systems",
                  desc: "Modular systems that integrate cleanly into existing infrastructure.",
                  badge: "Modular",
                  icon: <Server size={42} />
                },
                {
                  title: "Enterprise Control",
                  desc: "Admin panels, dashboards, and access control built for operational clarity.",
                  badge: "Clear Visibility",
                  icon: <LayoutDashboard size={42} />
                }
              ].map((slide, i) => (
                <div key={i} className={`CE-carousel-slide ${currentFeature === i ? "is-active" : ""}`}>
                  <div className="CE-slide-inner">
                    <div className="CE-slide-icon">{slide.icon}</div>
                    <h3 className="CE-slide-heading">{slide.title}</h3>
                    <p className="CE-slide-text">{slide.desc}</p>
                    <div className="CE-slide-meta">{slide.badge}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="CE-carousel-navigation">
              <button className="CE-nav-arrow prev" onClick={prevFeature} aria-label="Previous slide">
                <ChevronLeft size={28} />
              </button>

              <div className="CE-nav-pagination">
                {[0, 1, 2].map(i => (
                  <button
                    key={i}
                    className={`CE-page-dot ${currentFeature === i ? "is-active" : ""}`}
                    onClick={() => goToFeature(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              <button className="CE-nav-arrow next" onClick={nextFeature} aria-label="Next slide">
                <ChevronRight size={28} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT THIS REPLACES */}
      <section className="CE-replaces-section">
        <div className="container">
          <div className="CE-dark-box">
            <div className="replaces-text">
              <h2 className="replaces-title">What This Replaces</h2>
              <p className="replaces-lead">Breaking free from technical debt and vendor dependency.</p>
            </div>
            <div className="replaces-grid">
              {[
                { text: "Off‑the‑shelf SaaS limitations", icon: <ShieldCheck size={20} /> },
                { text: "Vendor lock‑in", icon: <Database size={20} /> },
                { text: "Fragile internal tools", icon: <Zap size={20} /> },
                { text: "Short‑term dev solutions", icon: <TrendingUp size={20} /> }
              ].map((item, i) => (
                <div key={i} className="replaces-item">
                  <div className="replaces-icon">{item.icon}</div>
                  <span className="replaces-label">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section className="CE-capabilities-section">
        <div className="container">
          <div className="CE-section-header centered">
            <span className="CE-section-badge">Capabilities</span>
            <h2 className="CE-section-title">What We Build</h2>
          </div>
          <div className="CE-capabilities-grid">
            {[
              {
                title: "Internal Dashboards",
                desc: "Custom admin systems designed to centralize controls and monitor KPIs in real-time.",
                icon: <LayoutDashboard size={24} color="white" />,
                colorClass: "magenta"
              },
              {
                title: "SaaS Platforms",
                desc: "Scalable multi-tenant architectures built to support mass user adoption and growth.",
                icon: <Globe size={24} color="white" />,
                colorClass: "purple"
              },
              {
                title: "Enterprise Portals",
                desc: "Secure, high-performance gateways for employees, partners, and client management.",
                icon: <Smartphone size={24} color="white" />,
                colorClass: "pink"
              },
              {
                title: "API Backends",
                desc: "High-throughput, modular backend systems that power your entire digital ecosystem.",
                icon: <Database size={24} color="white" />,
                colorClass: "indigo"
              }
            ].map((cap, i) => (
              <div key={i} className="CE-capability-card">
                <div className="card-top">
                  <div className={`CE-capability-icon ${cap.colorClass}`}>
                    {cap.icon}
                  </div>
                  <h3 className="CE-capability-title">{cap.title}</h3>
                </div>
                <p className="CE-capability-desc">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IDEAL FOR */}
      <section className="CE-ideal-section">
        <div className="container">
          <div className="CE-slider-header">
            <h2 className="CE-slider-title">Ideal For</h2>
            <p className="CE-slider-subtitle">Who we partner with to build the future of their infrastructure.</p>
          </div>
          <div className="CE-ideal-grid">
            {[
              {
                title: "Enterprises",
                desc: "Organizations with custom operational needs that generic software cannot solve.",
                icon: <Building2 />
              },
              {
                title: "Agencies",
                desc: "Forward-thinking teams building proprietary internal platforms to dominate their niche.",
                icon: <Settings2 />
              },
              {
                title: "Industry Leaders",
                desc: "High-performance teams replacing legacy systems with modern, agile architecture.",
                icon: <Users2 />
              }
            ].map((item, i) => (
              <div className="CE-ideal-card" key={i}>
                <div className="ideal-icon-box">{item.icon}</div>
                <h4 className="ideal-title">{item.title}</h4>
                <p className="ideal-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODEL */}
      <section className="CE-engagement-section">
        <div className="container">
          <div className="CE-slider-header">
            <h2 className="CE-engagement-heading">Engagement Model</h2>
            <p className="CE-slider-subtitle">A structured engineering process from scoping to scale.</p>
          </div>
          <div className="CE-steps-container">
            {[
              { title: "Technical Scoping", desc: "Deep discovery of business logic and tech specs." },
              { title: "Architecture", desc: "Custom design for scalability and security protocols." },
              { title: "Deployment", desc: "Secure rollout with zero-downtime integration." },
              { title: "Alignment", desc: "Ongoing support and long-term roadmap growth." }
            ].map((step, i) => (
              <div className="CE-step" key={i}>
                <div className="CE-step-circle">
                  <div className="CE-step-num">{i + 1}</div>
                  {i < 3 && <div className="CE-step-line"></div>}
                </div>
                <div className="CE-step-content">
                  <h4 className="CE-step-title">{step.title}</h4>
                  <p className="CE-step-desc">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustStatement />

      <div className="LD-divider"></div>
      <ChatbotWidget />
      <Call />
      <Footer />
    </div>
  );
}
